import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useFormState } from 'react-hook-form';
import { DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import {
  AutocompleteMultiSelect,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@shigo-ui/ui';

import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import { dashboardT } from '../../../../../lib/messages';
import {
  useSupplyIabCategoriesQuery,
  useSupplyMutations,
  useSupplyPlatformsQuery,
} from '../../hooks';
import { SupplyStatus, type SupplyApp, type SupplyAppPayload } from '../../model';
import { supplyAppFormSchema, type SupplyAppFormValue } from '../../formSchemas';

type SupplyAppDialogProps = {
  app: SupplyApp | null;
  onClose: () => void;
};

export function SupplyAppDialog({ app, onClose }: SupplyAppDialogProps) {
  'use no memo';

  const mutations = useSupplyMutations();
  const platforms = useSupplyPlatformsQuery();
  const categories = useSupplyIabCategoriesQuery();
  const form = useForm<SupplyAppFormValue>({
    defaultValues: {
      name: app?.name ?? '',
      bundleId: app?.bundleId ?? '',
      appStoreId: app?.appStoreId ?? '',
      appStoreUrl: app?.appStoreUrl ?? '',
      domain: app?.domain ?? '',
      platformId: app?.platform?.id ?? '',
      status: isSupplyStatus(app?.status) ? app.status : SupplyStatus.Active,
      iabCategories: app?.iabCategories ?? [],
    },
    resolver: zodResolver(supplyAppFormSchema),
  });
  const { errors } = useFormState({ control: form.control });
  const pending = mutations.createApp.isPending || mutations.updateApp.isPending;
  const nameId = React.useId();
  const bundleIdId = React.useId();
  const appStoreUrlId = React.useId();
  const appStoreIdId = React.useId();
  const domainId = React.useId();
  const { close: requestClose, onOpenChange } = useBusyDialogGuard(pending, onClose);

  async function submit(values: SupplyAppFormValue) {
    const platform = platforms.data?.find((item) => item.id === values.platformId);
    if (!platform) {
      const message = dashboardT('supply.platformUnavailable');
      form.setError('root.server', { message });
      toast.error(message);
      return;
    }
    const payload: SupplyAppPayload = {
      name: values.name.trim(),
      bundleId: values.bundleId.trim(),
      status: values.status,
      platform,
      iabCategories: values.iabCategories,
      ...(values.appStoreId.trim() ? { appStoreId: values.appStoreId.trim() } : {}),
      ...(values.appStoreUrl.trim() ? { appStoreUrl: values.appStoreUrl.trim() } : {}),
      ...(values.domain.trim() ? { domain: values.domain.trim() } : {}),
    };

    try {
      if (app) await mutations.updateApp.mutateAsync({ id: app.id, payload });
      else await mutations.createApp.mutateAsync(payload);
      toast.success(dashboardT(app ? 'supply.apps.updated' : 'supply.apps.created'));
      onClose();
    } catch {
      form.setError('root.server', { message: dashboardT('supply.saveError') });
    }
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] gap-0 overflow-visible p-0">
        <DialogHeader className="border-border border-b px-5 py-3.5">
          <DialogTitle className="text-[15px] font-semibold">
            {app ? dashboardT('supply.apps.edit') : dashboardT('supply.apps.create')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {dashboardT('supply.apps.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form noValidate onSubmit={(event) => void form.handleSubmit(submit)(event)}>
            <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
              <DashboardPageFormField
                label={dashboardT('supply.name')}
                required
                error={errors.name?.message}
                htmlFor={nameId}
              >
                <Input
                  id={nameId}
                  className="h-9"
                  required
                  aria-invalid={Boolean(errors.name)}
                  {...form.register('name')}
                />
              </DashboardPageFormField>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold">
                  {dashboardT('supply.status')} <span className="text-destructive">*</span>
                </Label>
                <Controller
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="h-9" aria-label={dashboardT('supply.status')}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SupplyStatus.Active}>
                          {dashboardT('supply.active')}
                        </SelectItem>
                        <SelectItem value={SupplyStatus.Archived}>
                          {dashboardT('supply.archived')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <DashboardPageFormField
                  label={dashboardT('supply.bundleId')}
                  required
                  error={errors.bundleId?.message}
                  htmlFor={bundleIdId}
                >
                  <Input
                    id={bundleIdId}
                    className="h-9"
                    required
                    aria-invalid={Boolean(errors.bundleId)}
                    {...form.register('bundleId')}
                  />
                </DashboardPageFormField>
                <div className="space-y-1.5">
                  <Label className="text-[13px] font-semibold">
                    {dashboardT('supply.platform')} <span className="text-destructive">*</span>
                  </Label>
                  <Controller
                    control={form.control}
                    name="platformId"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="h-9" aria-label={dashboardT('supply.platform')}>
                          <SelectValue placeholder={dashboardT('supply.selectPlatform')} />
                        </SelectTrigger>
                        <SelectContent>
                          {(platforms.data ?? []).map((item) => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.platformId?.message ? (
                    <p className="text-destructive text-xs">{errors.platformId.message}</p>
                  ) : null}
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <DashboardPageFormField
                  label={dashboardT('supply.appStoreUrl')}
                  htmlFor={appStoreUrlId}
                >
                  <Input id={appStoreUrlId} className="h-9" {...form.register('appStoreUrl')} />
                </DashboardPageFormField>
                <DashboardPageFormField
                  label={dashboardT('supply.appStoreId')}
                  htmlFor={appStoreIdId}
                >
                  <Input id={appStoreIdId} className="h-9" {...form.register('appStoreId')} />
                </DashboardPageFormField>
              </div>
              <DashboardPageFormField label={dashboardT('supply.domain')} htmlFor={domainId}>
                <Input id={domainId} className="h-9" {...form.register('domain')} />
              </DashboardPageFormField>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold">
                  {dashboardT('supply.iabCategories')}
                </Label>
                <Controller
                  control={form.control}
                  name="iabCategories"
                  render={({ field }) => (
                    <AutocompleteMultiSelect
                      label={dashboardT('supply.iabCategories')}
                      placeholder={dashboardT('supply.chooseIabCategories')}
                      options={(categories.data ?? []).map((item) => ({
                        id: item.id,
                        label: item.name,
                      }))}
                      value={field.value}
                      onValueChange={field.onChange}
                    />
                  )}
                />
              </div>
              {errors.root?.server?.message ? (
                <p role="alert" className="text-destructive text-sm">
                  {errors.root.server.message}
                </p>
              ) : null}
            </div>
            <DialogFooter className="border-border border-t px-5 py-3">
              <Button type="button" variant="outline" disabled={pending} onClick={requestClose}>
                {dashboardT('supply.cancel')}
              </Button>
              <Button type="submit" disabled={pending}>
                {app ? dashboardT('supply.save') : dashboardT('supply.apps.create')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function isSupplyStatus(value: string | undefined): value is SupplyStatus {
  return value === SupplyStatus.Active || value === SupplyStatus.Archived;
}
